import { useMemo } from 'react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useStore } from '@/state/store';
import { project } from '@/engine';
import { fmtCurrency, fmtCurrencyCompact, fmtPct, fmtRatioPct } from '@/lib/format';
import type { ProjectionResult, Scenario } from '@/types';
import { COLORS, downsample, useIsDark, yearAxisProps, TOOLTIP_PROPS } from './charts/chartUtils';

const SERIES_COLORS = [COLORS.netWorth, COLORS.sell, COLORS.portfolio];

interface Compared {
  scenario: Scenario;
  result: ProjectionResult;
  color: string;
}

/** 2–3 way comparison: overlaid charts + a side-by-side table (§10.5). */
export function CompareView() {
  const selection = useStore((s) => s.ui.comparisonSelection);
  const scenarios = useStore((s) => s.scenarios);
  const dark = useIsDark();

  const compared = useMemo<Compared[]>(() => {
    return selection
      .map((id, i) => {
        const scenario = scenarios.find((s) => s.id === id);
        if (!scenario) return null;
        return { scenario, result: project(scenario.inputs), color: SERIES_COLORS[i % SERIES_COLORS.length]! };
      })
      .filter((x): x is Compared => x !== null);
  }, [selection, scenarios]);

  if (compared.length === 0) {
    return <p className="text-sm text-slate-500">Select up to 3 scenarios in the library to compare them here.</p>;
  }

  const overlay = (pick: (r: ProjectionResult, m: number) => number) => {
    const maxLen = Math.max(...compared.map((c) => c.result.rows.length));
    const idx = downsample(Array.from({ length: maxLen }, (_, i) => i));
    return idx.map((m) => {
      const point: Record<string, number> = { year: +(m / 12).toFixed(2) };
      for (const c of compared) {
        const row = c.result.rows[m];
        if (row) point[c.scenario.name] = pick(c.result, m);
      }
      return point;
    });
  };

  const Chart = ({ title, data }: { title: string; data: Record<string, number>[] }) => (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <h3 className="mb-2 text-sm font-semibold">{title}</h3>
      <div className="h-56">
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 5, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid stroke={dark ? '#334155' : '#e2e8f0'} strokeDasharray="3 3" />
            <XAxis stroke={dark ? '#94a3b8' : '#64748b'} fontSize={11} {...yearAxisProps(data)} />
            <YAxis stroke={dark ? '#94a3b8' : '#64748b'} fontSize={11} tickFormatter={fmtCurrencyCompact} width={56} />
            <Tooltip {...TOOLTIP_PROPS} formatter={(v: number | string) => fmtCurrency(Number(v))} />
            <Legend />
            {compared.map((c) => (
              <Line key={c.scenario.id} type="monotone" dataKey={c.scenario.name} stroke={c.color} dot={false} strokeWidth={2} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Chart title="Net worth" data={overlay((r, m) => r.rows[m]!.netWorth)} />
        <Chart title="Balance" data={overlay((r, m) => r.rows[m]!.endBalance)} />
        <Chart title="Cumulative interest" data={overlay((r, m) => r.rows[m]!.cumulativeInterest)} />
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <table className="w-full text-left text-xs">
          <thead className="text-slate-500">
            <tr>
              <th className="pr-3">Scenario</th>
              <th className="pr-3">Start APR</th>
              <th className="pr-3">Eff. spread</th>
              <th className="pr-3">Ending net worth</th>
              <th className="pr-3">Total interest</th>
              <th className="pr-3">Worst LTV</th>
              <th className="pr-3">Min cushion</th>
              <th className="pr-3">First call</th>
              <th className="pr-3">Estate diff</th>
            </tr>
          </thead>
          <tbody>
            {compared.map((c) => {
              const s = c.result.summary;
              return (
                <tr key={c.scenario.id} className="border-t border-slate-100 dark:border-slate-700">
                  <td className="pr-3 py-1 font-medium" style={{ color: c.color }}>{c.scenario.name}</td>
                  <td className="pr-3 tabular-nums">{fmtPct(s.startApr)}</td>
                  <td className="pr-3 tabular-nums">{fmtPct(s.effectiveSpread)}</td>
                  <td className="pr-3 tabular-nums">{fmtCurrency(s.endingNetWorth)}</td>
                  <td className="pr-3 tabular-nums">{fmtCurrency(s.totalInterest)}</td>
                  <td className="pr-3 tabular-nums">{fmtRatioPct(s.worstLtv)}</td>
                  <td className="pr-3 tabular-nums">{fmtRatioPct(s.minCallCushionPct)}</td>
                  <td className="pr-3 tabular-nums">{s.firstMaintenanceCallMonth ?? '—'}</td>
                  <td className="pr-3 tabular-nums">{fmtCurrency(c.result.estate.difference)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
